import { useCallback, useEffect, useState } from 'react';

import { anyAssetUpdated } from 'src/application/ContentZone/components/Widgets/_shared/Questions/hooks/anyAssetUpdated';
import { usePrevious } from 'src/application/hooks/usePrevious';
import { Guid } from 'src/application/store/model/guid';

import { WidgetDataRepository } from '../../../store/repository/widgets/widgetDataRepository';
import { ignorePromise, WidgetType } from '@ael/core-api';

type Dimensions = { width: number; height: number };
type SupportedAssetTypes = 'audio' | 'video' | 'image';
export type AssetData = {
  url: string;
  type: 'audio' | 'video' | 'image';
  dimensions: Dimensions | null;
};

export type GetAssetUrlForAssetIndex = (assetIndex: number) => string | null;
export type GetAssetDataForIndex = (assetIndex: number) => AssetData | null;

type InitData = {
  assetsUrls: Array<string | null>;
  assetsTypes: Array<SupportedAssetTypes | null>;
  assetsDimensions: Array<Dimensions | null>;
};

/**
 * Hook that will receive an array of asset ids and returns array of urls for those asset ids.
 * For example [Guid1, null, Guid2] and returns an array [Url1, null, Url2].
 */
export const useGetMultipleAssetUrls = (widgetId: Guid, widgetType: WidgetType, assetIds: Array<string | null>) => {
  const [data, setData] = useState<InitData>({ assetsUrls: [], assetsTypes: [], assetsDimensions: [] });
  const prevAssetIds = usePrevious(assetIds);
  const clearData = () => {
    setData({ assetsUrls: [], assetsTypes: [], assetsDimensions: [] });
  };
  const fetchAssetUrls = useCallback(async () => {
    if (assetIds.filter((x) => x !== null).length === 0) {
      setData({ assetsUrls: [], assetsTypes: [], assetsDimensions: [] });
      return;
    }
    await WidgetDataRepository.getWidgetData({ assets: assetIds }, widgetType, widgetId).then((data: unknown) => {
      const typedData = data as InitData;
      setData({
        assetsUrls: typedData.assetsUrls,
        assetsTypes: typedData.assetsTypes,
        assetsDimensions: typedData.assetsDimensions,
      });
    });
  }, [assetIds, widgetId, widgetType]);

  useEffect(() => {
    if (anyAssetUpdated(prevAssetIds, assetIds)) {
      clearData();
      ignorePromise(fetchAssetUrls());
    }
  }, [fetchAssetUrls, prevAssetIds, assetIds]);

  const getAssetUrlForAssetIndex: GetAssetUrlForAssetIndex = useCallback(
    (assetIndex: number) => {
      return data.assetsUrls[assetIndex];
    },
    [data.assetsUrls]
  );

  const getAssetDataForIndex: GetAssetDataForIndex = useCallback(
    (assetIndex: number) => {
      const dimensions = data.assetsDimensions[assetIndex];
      const url = data.assetsUrls[assetIndex];
      const type = data.assetsTypes[assetIndex];
      if (url && type) {
        return {
          url,
          type,
          dimensions: dimensions ? { height: dimensions.height, width: dimensions.width } : null,
        };
      }
      return null;
    },
    [data.assetsUrls, data.assetsTypes, data.assetsDimensions]
  );

  return { getAssetUrlForAssetIndex, getAssetDataForIndex };
};

export const useGetSingleImageAssetData = (widgetId: Guid, widgetType: WidgetType, assetId: string | null) => {
  const [data, setData] = useState<AssetData | null>(null);
  const prevAssetId = usePrevious(assetId);
  const fetchAssetUrls = useCallback(async () => {
    if (assetId === null) {
      setData(null);
      return;
    }
    await WidgetDataRepository.getWidgetData({ assetId: assetId }, widgetType, widgetId).then((data: unknown) => {
      const response = data as { assetUrl: string; assetName: string; imageHeight: number; imageWidth: number };
      const assetData: AssetData = {
        url: response.assetUrl,
        type: 'image',
        dimensions: { height: response.imageHeight, width: response.imageWidth },
      };
      setData(assetData);
    });
  }, [assetId, widgetId, widgetType]);

  useEffect(() => {
    if (assetId !== prevAssetId) {
      setData(null);
      ignorePromise(fetchAssetUrls());
    }
  }, [fetchAssetUrls, prevAssetId, assetId]);

  return data;
};
